import { memo, Suspense, useState } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, Clock, PlusCircle, Ban, Plus, ShieldAlert } from 'lucide-react';
import DropdownButton from '../Common/DropDown';
import { getStatusColor } from '../Common/standUpCommonFunctions';

/** Converts #RRGGBB to rgba for reliable tint backgrounds (8-digit hex is inconsistent in some browsers). */
function hexToRgba(hex, alpha) {
  const normalized = String(hex).replace('#', '');
  if (normalized.length !== 6) return `rgba(0, 0, 0, ${alpha})`;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const CATEGORY_FILTERS = [
  {
    id: 'Overdue',
    label: 'Overdue',
    color: '#F59F12',
    Icon: AlertTriangle,
  },
  {
    id: 'Planned',
    label: 'Planned',
    color: '#48A7FF',
    Icon: Clock,
  },
  {
    id: 'Added',
    label: 'Added',
    color: '#64D518',
    Icon: PlusCircle,
  },
  {
    id: 'Blocker',
    label: 'Blocker',
    color: '#E11D48',
    Icon: ShieldAlert,
  },
  {
    id: 'Removed',
    label: 'Removed',
    color: '#E42626',
    Icon: Ban,
  },
];

function StatusBadges({ statusData, selectedStatuses, onToggle, colorMap, badgesRef }) {
  return (
    <div ref={badgesRef} className="contents">
      {statusData?.map(({ status, count }, badgeIndex) => {
        const isSelected = selectedStatuses.includes(status);
        const statusColor = getStatusColor(status, colorMap);

        return (
          <button
            type="button"
            key={`${String(status)}-${badgeIndex}`}
            onClick={() => onToggle(status)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md whitespace-nowrap text-sm font-medium transition-colors duration-200
              ${
                isSelected
                  ? 'bg-[#066FD1] text-white'
                  : 'bg-[#E8EEF4] text-[#24527A] dark:bg-[#293345] dark:text-[#E8EEF4]'
              }`}
            title={`${count} ${status}`}
          >
            <span
              className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${statusColor.tailwind}`}
              style={statusColor.hex ? { backgroundColor: statusColor.hex } : undefined}
            />
            <span>
              {count} {status}
            </span>
          </button>
        );
      })}
    </div>
  );
}

StatusBadges.propTypes = {
  statusData: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    }),
  ).isRequired,
  selectedStatuses: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  colorMap: PropTypes.instanceOf(Map).isRequired,
  badgesRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};

function CategoryBadges({ selectedCategories, onToggle, categoryCounts }) {
  const [hoveredId, setHoveredId] = useState(null);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {CATEGORY_FILTERS.map(({ id, label, color, Icon }) => {
        const isSelected = selectedCategories.includes(id);
        const isHovered = hoveredId === id;

        let backgroundColor = 'transparent';
        if (isSelected) {
          backgroundColor = color;
        } else if (isHovered) {
          backgroundColor = hexToRgba(color, 0.28);
        } else {
          backgroundColor = hexToRgba(color, 0.1);
        }

        const count = categoryCounts?.[id] ?? 0;
        if (count <= 0) return null;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onToggle(id)}
            onMouseEnter={() => setHoveredId(id)}
            onMouseLeave={() => setHoveredId(null)}
            className={`standup-category-filter-btn inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium border transition-colors duration-200 ${
              isSelected ? 'is-selected' : ''
            }`}
            style={{
              borderColor: color,
              color: isSelected ? '#ffffff' : color,
              backgroundColor,
            }}
            title={`${count} ${label}`}
            aria-pressed={isSelected}
          >
            <Icon size={14} strokeWidth={2} aria-hidden />
            {count} {label}
          </button>
        );
      })}
    </div>
  );
}

CategoryBadges.propTypes = {
  selectedCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  categoryCounts: PropTypes.object,
};

function StandUpJiraTableSection({
  theme,
  selectedStatuses,
  onToggleStatus,
  selectedCategories,
  onToggleCategory,
  categoryBadgeCounts,
  colorMap,
  badgesRef,
  shouldShowArrows,
  isStatusAtStart,
  isStatusAtEnd,
  onStatusCarouselPrev,
  onStatusCarouselNext,
  getVisibleStatusItems,
  allColumns,
  visibleColumns,
  onColumnToggle,
  onColumnsClearAll,
  isColumnSelectorOpen,
  setIsColumnSelectorOpen,
  columnSelectorRef,
  heavyChartsReady,
  chartSuspenseFallback,
  AGrid,
  filteredRowData,
  columnDefs,
  defaultColDef,
  onJiraGridApiReady,
  onFilterChanged,
  clearFiltersSignal,
  aGridRef,
  statusCarouselContainerRef,
  hideCategoryBadges = false,
}) {
  const totalRecords = filteredRowData?.length ?? 0;

  return (
    <div
      id="jira-table"
      ref={statusCarouselContainerRef}
      className="ml-5 p-3 bg-[#FFFFFF] dark:bg-[#182433] dark:text-[#C8C8C8] text-[#24527A] rounded-lg shadow-[0_1px_20px_rgba(0,0,0,0.1)] dark:shadow-md border border-[#D1E2F0] dark:border-slate-700 w-full flex-1 relative jira-table-panel"
    >
      <div className="mb-2">
        <div className="flex items-center">
          {shouldShowArrows && (
            <button
              type="button"
              onClick={onStatusCarouselPrev}
              disabled={isStatusAtStart}
              className="flex-shrink-0 w-8 h-8 p-2 rounded-md flex items-center justify-center transition-colors duration-200 mr-3 text-[#182433] dark:text-white opacity-60 disabled:opacity-30 disabled:cursor-not-allowed dark:hover:bg-[#293345] hover:bg-[#e6e6e6] hover:opacity-100"
              aria-label="Previous status badges"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          <div
            className={`flex-1 min-w-0 max-h-48 overflow-hidden ${
              shouldShowArrows ? 'pr-4' : 'px-4'
            }`}
          >
            <div className="flex flex-wrap gap-2 p-2 -ml-2 -mt-1">
              <StatusBadges
                statusData={getVisibleStatusItems()}
                selectedStatuses={selectedStatuses}
                onToggle={onToggleStatus}
                colorMap={colorMap}
                badgesRef={badgesRef}
              />
            </div>
          </div>

          {shouldShowArrows && (
            <button
              type="button"
              onClick={onStatusCarouselNext}
              disabled={isStatusAtEnd}
              className="flex-shrink-0 w-8 h-8 p-2 rounded-md flex items-center justify-center transition-colors duration-200 ml-3 text-[#182433] dark:text-white opacity-60 disabled:opacity-30 disabled:cursor-not-allowed dark:hover:bg-[#293345] hover:bg-[#e6e6e6] hover:opacity-100"
              aria-label="Next status badges"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          <div
            className={`flex-shrink-0 ${shouldShowArrows ? 'ml-4' : 'ml-2'} relative z-10`}
          >
            <DropdownButton
              variant="icon"
              type="columns"
              title="Add Column"
              icon={
                <span className="inline-flex items-center justify-center w-full h-full text-[#066FD1]">
                  <Plus size={18} strokeWidth={2.5} />
                </span>
              }
              options={allColumns
                .filter((col) => col.allowToggle)
                .map((col) => ({
                  value: col.field,
                  label: col.headerName,
                  checked: visibleColumns.includes(col.field),
                  disabled:
                    visibleColumns.length === 1 && visibleColumns.includes(col.field),
                }))}
              onSelect={onColumnToggle}
              onClearAll={onColumnsClearAll}
              isOpen={isColumnSelectorOpen}
              setIsOpen={setIsColumnSelectorOpen}
              reference={columnSelectorRef}
              showSearch={true}
            />
          </div>
        </div>

          <div className="mt-2">
            {!hideCategoryBadges && (
              <CategoryBadges
                selectedCategories={selectedCategories}
                onToggle={onToggleCategory}
                categoryCounts={categoryBadgeCounts}
              />
            )}
          </div>
        </div>
      <div className="jira-table-grid-wrap -mx-1">
        {heavyChartsReady ? (
          <Suspense fallback={chartSuspenseFallback}>
            <AGrid
              rowData={filteredRowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              tooltipShowDelay={100}
              onApiReady={onJiraGridApiReady}
              tooltipMouseTrack
              domLayout="normal"
              height="350px"
              initialPageSize={7}
              disableAutoPageSize
              onFilterChanged={onFilterChanged}
              preventColumnDrag={false}
              enableColumnDragRestore={true}
              clearFiltersSignal={clearFiltersSignal}
              ref={aGridRef}
              theme={theme}
            />
          </Suspense>
        ) : (
          chartSuspenseFallback
        )}
        <div
          className={`jira-table-total-records text-sm px-2 pointer-events-none ${
            theme === 'light' ? 'text-[#24527A]' : 'text-[#C8C8C8]'
          }`}
        >
          Total Records: {totalRecords}
        </div>
      </div>
    </div>
  );
}

StandUpJiraTableSection.propTypes = {
  theme: PropTypes.string,
  selectedStatuses: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleStatus: PropTypes.func.isRequired,
  selectedCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleCategory: PropTypes.func.isRequired,
  categoryBadgeCounts: PropTypes.object,
  colorMap: PropTypes.instanceOf(Map).isRequired,
  badgesRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  shouldShowArrows: PropTypes.bool,
  isStatusAtStart: PropTypes.bool,
  isStatusAtEnd: PropTypes.bool,
  onStatusCarouselPrev: PropTypes.func,
  onStatusCarouselNext: PropTypes.func,
  getVisibleStatusItems: PropTypes.func.isRequired,
  allColumns: PropTypes.array,
  visibleColumns: PropTypes.array,
  onColumnToggle: PropTypes.func,
  onColumnsClearAll: PropTypes.func,
  isColumnSelectorOpen: PropTypes.bool,
  setIsColumnSelectorOpen: PropTypes.func,
  columnSelectorRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  heavyChartsReady: PropTypes.bool,
  chartSuspenseFallback: PropTypes.node,
  AGrid: PropTypes.elementType,
  filteredRowData: PropTypes.array,
  columnDefs: PropTypes.array,
  defaultColDef: PropTypes.object,
  onJiraGridApiReady: PropTypes.func,
  onFilterChanged: PropTypes.func,
  clearFiltersSignal: PropTypes.number,
  aGridRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.object }),
  ]),
  statusCarouselContainerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  hideCategoryBadges: PropTypes.bool,
};

export default memo(StandUpJiraTableSection);
